import { useAccount } from 'wagmi'
import TokenLogo from '~/components/TokenLogo'
import { Column } from '~/components/Layout'
import { formatFormAmount } from '~/utils/numbers'
import { truncateAddress } from '~/utils/truncateAddress'
import { getNetworkLogo } from '~/utils/chain'
import { formatAmount } from '~/components/Exchange/helpers'
import { OrderedSpotSwapToken } from './types'
import styled from 'styled-components'

interface Props {
  token: OrderedSpotSwapToken
  searchText: string
  showNetworkBadge: boolean
  onSelect: (token: ERC20Token) => void
}

const TokenListItem: React.FC<Props> = ({
  token,
  searchText,
  showNetworkBadge,
  onSelect,
}) => {
  const { address: account } = useAccount()
  const { chainId, logo, symbol, balance, priceUSD, address, name } = token
  const networkLogo = getNetworkLogo(chainId)

  return (
    <ListItem onClick={() => onSelect(token)}>
      <TokenLogo
        large
        logo={logo}
        symbol={symbol}
        space={0}
        networkLogo={networkLogo}
        showNetworkBadge={showNetworkBadge}
      />

      <div className="item-right">
        <Column>
          <StyledSymbol data-testid="spotswap-token-list-item-symbol">
            {symbol}
          </StyledSymbol>

          <span
            className="token-name"
            data-testid="spotswap-token-list-item-name"
          >
            {searchText && searchText.startsWith('0x')
              ? truncateAddress(address)
              : name}
          </span>
        </Column>
        {account && !!Number(balance) ? (
          <div
            className="token-account-info"
            data-testid="spotswap-token-list-item-account-info"
          >
            <span
              className="balance"
              data-testid="spotswap-token-list-item-balance"
            >
              {formatFormAmount(balance, 4)}
            </span>
            {!!Number(priceUSD) && (
              <span
                className="priceUSD"
                data-testid="spotswap-token-list-item-priceUSD"
              >
                ${formatAmount(priceUSD, 2)}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </ListItem>
  )
}

export default TokenListItem

const ListItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgSurface};
  }

  .item-right {
    border-bottom: 0.5px solid ${({ theme }) => theme.colors.borderSubtle};
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    padding: 17px 0;
    padding-right: 17.5px;
    min-height: 70px;
    margin-left: 8px;
  }

  .token-name {
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  .token-user-add {
    font-weight: 300;
    font-size: 13px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  .token-account-info {
    display: inline-flex;
    flex-direction: column;
    text-align: right;
  }

  .balance {
    font-weight: 500;
    font-size: 17px;
    line-height: 15px;
    color: ${({ theme }) => theme.colors.textTitle};
    margin: 0;
  }

  .priceUSD {
    font-weight: normal;
    font-size: 13px;
    line-height: 11px;
    color: ${({ theme }) => theme.colors.textTertiary};
    margin-top: 9px;
  }
`

const StyledSymbol = styled.div`
  font-size: 17px;
  line-height: 23.15px;
  color: ${({ theme }) => theme.colors.textTitle};
`
